import React, { useState } from 'react';
import axios from 'axios';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import { v4 as uuidv4 } from 'uuid';
import showdown from 'showdown';

const baseApiUri = process.env.REACT_APP_API_URL;
const converter = new showdown.Converter();

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export default function Assessments() {
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState('');
  const [completionId, setCompletionId] = useState(null);
  const [status, setStatus] = useState('idle');
  const [assessmentHtml, setAssessmentHtml] = useState(null);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [requestSortKey, setRequestSortKey] = useState(0);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setAssessmentHtml(null);
    setCompletionId(null);
    setStatus('idle');
    setError(null);
  };

  const handleSubmit = async () => {
    if (!file && !fileUrl.trim()) return;

    setStatus('uploading');

    try {
      const formData = new FormData();
      const generatedId = uuidv4();

      formData.append('key', '09848734-8745-afrt-8745-8745873487');
      formData.append('sortKey', parseInt(requestSortKey));
      formData.append('query', 'Generate a needs assessment from this document.');
      formData.append('type', 'needs-assessment');
      formData.append('stream', 'true');
      formData.append('id', generatedId);

      if (file) {
        formData.append('filename', file);
      }

      if (fileUrl.trim()) {
        formData.append('fileurl', fileUrl.trim());
      }

      const response = await axios.post(`${baseApiUri}/ai/completion`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { id, sortKey } = response.data;
      setCompletionId(generatedId);
      console.log(completionId);
      setStatus('polling');

      pollForAssistantResponse(id, sortKey);
    } catch (err) {
      console.error(err);
      setStatus('error');
      setError('Failed to submit the file.');
    }
  };

  const pollForAssistantResponse = async (id, sortKey) => {
    let attempt = 0;
    console.log(attempt);
    let hasMore = true;

    const poll = async () => {
      try {
        while (hasMore) {
          const endpoint = `${baseApiUri}/ai/completion/${id}/${sortKey}`;
          const completion = await axios.get(endpoint);
          const { status: compStatus, message } = completion.data.response;

          setMessages(prev => {
            const exists = prev.some(msg => msg.text === 'Analyzing the data...');
            return exists ? prev : [...prev, { sender: 'assistant', text: 'Analyzing the data...' }];
          });

          if (message) {
            const messageHtml = converter.makeHtml(message);
            console.log(messages);
            setMessages(prev => {
              return prev.map((msg, i) =>
                i === prev.length - 1 && msg.sender === 'assistant'
                  ? { ...msg, text: messageHtml.replaceAll('<a', '<a target="_blank" ') }
                  : msg
              );
            });
          }

          if (compStatus === 'success') {
            const finalHtml = converter.makeHtml(message);
            setAssessmentHtml(finalHtml);
            setMessages(prev => {
              return prev.map((msg, i) =>
                i === prev.length - 1 && msg.sender === 'assistant'
                  ? { ...msg, text: finalHtml.replaceAll('<a', '<a target="_blank" ') }
                  : msg
              );
            });
            setStatus('completed');
            setRequestSortKey(prev => prev + 1);
            hasMore = false;
          }

          attempt++;
          await sleep(500);
        }
      } catch (error) {
        console.error('Polling failed:', error);
        setMessages(prev => [...prev, { sender: 'assistant', text: 'The assistant did not respond. Please try again later.' }]);
      }
    };

    poll();
  };

  return (
    <Container maxWidth="sm" sx={{ padding: '2rem' }}>
      <Paper sx={{ padding: '2rem' }} elevation={3}>
        <Typography variant="h5" gutterBottom>
          📄 Upload a File for Needs Assessment
        </Typography>

        <Stack spacing={2}>
          <TextField
            type="file"
            onChange={handleFileChange}
            inputProps={{ accept: '.pdf,.doc,.docx,.txt,.md' }}
            fullWidth
          />

          <Typography variant="body2" color="text.secondary">
            If your attachment is larger than 10 MB, please provide a URL to your document instead:
          </Typography>

          <TextField
            label="Document URL"
            variant="outlined"
            fullWidth
            value={fileUrl}
            onChange={(e) => setFileUrl(e.target.value)}
            placeholder="https://example.com/my-large-document.pdf"
          />

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={(status === 'uploading' || (!file && !fileUrl))}
          >
            Generate Assessment
          </Button>
        </Stack>

        {status === 'uploading' && (
          <Box mt={2}>
            <CircularProgress size={24} />
            <Typography variant="body2">Uploading file...</Typography>
          </Box>
        )}

        {status === 'polling' && (
          <Box mt={2}>
            <CircularProgress size={24} />
            <Typography variant="body2">Generating assessment, please wait...</Typography>
          </Box>
        )}

        {status === 'error' && (
          <Box mt={2}>
            <Typography variant="body2" color="error">{error}</Typography>
          </Box>
        )}

        {status === 'completed' && assessmentHtml && (
          <Box mt={4} sx={{ border: '1px solid #ccc', padding: '1rem' }}>
            <Typography variant="h6" gutterBottom>
              ✅ Assessment Complete
            </Typography>
            <div dangerouslySetInnerHTML={{ __html: assessmentHtml }} />
          </Box>
        )}
      </Paper>
    </Container>
  );
}


